import { action } from 'mobx'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { task } from 'mobx-task'
import { type WorkspacePostingStore } from '@/features/admin/staffing/WorkspacePostingStore'
import type {
  LocationOption,
  PrimarySoftwareOption,
  SpecialtyOption,
  SupplementalSoftwareOption,
} from '@/features/admin/staffing/workspace-posting-field-options'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { WorkspacePostingDialogViewModel } from '@/features/admin/staffing/components/WorkspacePostingDialog/WorkspacePostingDialogViewModel'
import { type WorkspacePosting } from '@/features/admin/staffing/WorkspacePosting'

export class UpdateWorkspacePostingDialogViewModel extends WorkspacePostingDialogViewModel {
  constructor(
    private readonly _workspacePostingStore: WorkspacePostingStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _refreshStatusFilter: () => Promise<void>,
    getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
  ) {
    super(getWorkspaceMemberships)
  }

  /**
   * Show the update workspace posting dialog with populated fields.
   * @param workspacePosting
   */
  @action.bound
  showWithData(workspacePosting: WorkspacePosting) {
    this.workspacePosting = workspacePosting
    this.workspace = workspacePosting.workspace
    this.location = workspacePosting.location as LocationOption
    this.primarySoftware =
      workspacePosting.primarySoftware as PrimarySoftwareOption
    this.backupRequired = workspacePosting.backupFileNeeded
    this.supplementalSoftware =
      workspacePosting.supplementalSoftware as SupplementalSoftwareOption
    this.specialties = workspacePosting.specialties as SpecialtyOption[]
    this.prosRequired = workspacePosting.capacity
    this.maxConcurrentWorkspaces = workspacePosting.maxAllowedWorkspaces
    this.typeOfWork = workspacePosting.jobTypes.filter((t) => t !== 'Null')
    this.description = workspacePosting.description
    this.opportunityType = workspacePosting.opportunityType

    this.modal.show()
  }

  /**
   * Update a workspace posting.
   */
  submit = task.resolved(async () => {
    if (!this.validate() || this.workspacePosting?.status !== 'Draft') {
      return
    }

    await this._workspacePostingStore
      .updateWorkspacePosting(this.workspacePosting.id, this.details)
      .catch(this._createAlertErrorHandler('Updating the posting failed'))

    void this._refreshStatusFilter()
    this._newAlert()
      .withState('success')
      .saying('Success', 'Workspace posting updated.')
      .autoDismiss()
      .show()
    this.close()
  })
}
